import React, { useEffect } from 'react';

import {
  telemetryClickHandler,
  telemetryImpressionHandler,
} from '../telemetryHandler';
import type { DropdownSection, NavLink, RichNavItem } from '../types';

interface LayeredMenuProps {
  sections: DropdownSection[];
}

function isSectionRich(items: RichNavItem[]): boolean {
  return items.some(
    (item) => item.icon || item.stockblocksIcon || item.description
  );
}

function LayeredMenuItem({ item }: { item: RichNavItem }): JSX.Element {
  useEffect(() => {
    telemetryImpressionHandler(item.telemetrySlug, item.telemetryEvents);
  }, []);

  const link = (
    <a
      key={item.href}
      href={item.href}
      className="flex items-center gap-3 hover:opacity-80 no-underline"
      onClick={telemetryClickHandler(item.telemetrySlug, item.telemetryEvents)}
    >
      {item.stockblocksIcon && (
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-md">
          <img src={item.stockblocksIcon} alt="" width={24} height={24} />
        </div>
      )}
      <div className="flex flex-col gap-0.5">
        <span className="whitespace-nowrap text-base font-semibold text-gray-900">
          {item.label}
        </span>
        {item.description && (
          <span className="text-xs text-gray-800" style={{ width: '330px' }}>
            {item.description}
          </span>
        )}
      </div>
    </a>
  );

  if (!item.subItems || item.subItems.length === 0) {
    return link;
  }

  return (
    <div className="flex flex-col gap-4">
      {link}
      <div className="flex flex-col gap-4 pl-12">
        {item.subItems.map((subItem) => (
          <LayeredMenuSubItem key={subItem.href} item={subItem} />
        ))}
      </div>
    </div>
  );
}

function LayeredMenuSubItem({ item }: { item: NavLink }): JSX.Element {
  useEffect(() => {
    telemetryImpressionHandler(item.telemetrySlug, item.telemetryEvents);
  }, []);

  return (
    <a
      href={item.href}
      className="text-sm text-gray-900 hover:opacity-80 no-underline"
      onClick={telemetryClickHandler(item.telemetrySlug, item.telemetryEvents)}
    >
      {item.label}
    </a>
  );
}

export default function LayeredMenu({
  sections,
}: LayeredMenuProps): JSX.Element {
  return (
    <div className="flex flex-col p-8 gap-4 rounded-2xl">
      {sections.map((section, index) => {
        const isRich = isSectionRich(section.items);
        const hasTitle = !!section.title;
        return (
          <div key={section.title || `section-${index}`}>
            {/* Section header + divider — only when the section has a title.
                Headerless sections (e.g. AI Tools) skip this so the dropdown
                trigger label isn't repeated inside the panel. */}
            {hasTitle && (
              <div className="flex flex-col gap-2 pt-4 first:pt-0">
                <span className="pb-2 text-sm text-gray-900">
                  {section.title}
                </span>
                <div className="h-px bg-gray-200" />
              </div>
            )}

            {/* Section items — gap-4 for plain links, gap-6 for rich items */}
            <div className={`flex flex-col gap-4 ${hasTitle ? 'pt-4' : ''}`}>
              {section.items.map((item) => (
                <LayeredMenuItem key={item.href} item={item} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
